import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const BranchList = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [showDisabled, setShowDisabled] = useState(false);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoading(true);
        const data = await apiService.getBranches();
        setBranches(data);
        setFilteredBranches(data.filter(branch => branch.is_enabled || showDisabled));
      } catch (err) {
        console.error('Error al cargar sucursales:', err);
        setError('Error al cargar las sucursales. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchBranches();
  }, [showDisabled]);

  useEffect(() => {
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = branches.filter(
        branch =>
          branch.branch_name.toLowerCase().includes(lowercasedSearch) &&
          (branch.is_enabled || showDisabled)
      );
      setFilteredBranches(filtered);
    } else {
      setFilteredBranches(branches.filter(branch => branch.is_enabled || showDisabled));
    }
    setPage(0);
  }, [searchTerm, branches, showDisabled]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleCreateBranch = () => {
    navigate('/branches/create');
  };

  const handleEditBranch = (id) => {
    navigate(`/branches/edit/${id}`);
  };

  const handleViewBranch = (id) => {
    navigate(`/branches/view/${id}`);
  };

  const handleToggleStatus = async (id, isEnabled) => {
    try {
      if (isEnabled) {
        await apiService.disableBranch(id);
      } else {
        await apiService.enableBranch(id);
      }
      
      // Actualizar la lista de sucursales
      const updatedBranches = branches.map(branch => {
        if (branch.id === id) {
          return { ...branch, is_enabled: !isEnabled };
        }
        return branch;
      });
      
      setBranches(updatedBranches);
    } catch (err) {
      console.error('Error al cambiar estado de la sucursal:', err);
      setError('Error al cambiar el estado de la sucursal. Por favor, intente nuevamente.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Sucursales</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateBranch}
        >
          Nueva Sucursal
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <TextField
            variant="outlined"
            placeholder="Buscar por nombre..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: '70%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
                color="primary"
              />
            }
            label="Mostrar deshabilitadas"
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Ubicación</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Fecha de Creación</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBranches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No se encontraron sucursales
                </TableCell>
              </TableRow>
            ) : (
              filteredBranches
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((branch) => (
                  <TableRow key={branch.id}>
                    <TableCell>{branch.branch_name}</TableCell>
                    <TableCell>
                      {branch.district_name ? 
                        `${branch.district_name}, ${branch.province_name}, ${branch.department_name}` : 
                        'Sin ubicación'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={branch.is_enabled ? 'Activa' : 'Inactiva'}
                        color={branch.is_enabled ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(branch.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalles">
                        <IconButton
                          color="info"
                          onClick={() => handleViewBranch(branch.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditBranch(branch.id)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={branch.is_enabled ? 'Deshabilitar' : 'Habilitar'}>
                        <Switch
                          checked={branch.is_enabled}
                          onChange={() => handleToggleStatus(branch.id, branch.is_enabled)}
                          color="primary"
                          size="small"
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredBranches.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default BranchList; 